import { it } from "../../i18n/it/index.ts";
import type { Traduttore } from "../../i18n/index.ts";
import type { AuditEntry } from "./types.ts";

/** Una frase è una sequenza di testo e, al più, un link a uno stack. */
export type Segmento = string | { readonly stack: string; readonly id: number };

/**
 * Le azioni che hanno una frase: le chiavi `attivita.<azione>` con almeno un
 * punto nell'azione, senza le etichette del filtro (`attivita.filtro.*`).
 */
export const AZIONI_NOTE: readonly string[] = Object.keys(it)
  .filter((k) => k.startsWith("attivita.") && k.slice(9).includes(".") && !k.startsWith("attivita.filtro."))
  .map((k) => k.slice(9));

const SEGNAPOSTO = "\u0000";

/**
 * La voce del registro come frase leggibile.
 *
 * Per un'azione sconosciuta, o con dettagli imprevisti, ripiega sul codice:
 * la pagina non deve cadere per una voce scritta da un server più nuovo.
 */
export function fraseAttivita(voce: AuditEntry, t: Traduttore): Segmento[] {
  if (!AZIONI_NOTE.includes(voce.action)) return [voce.action];

  const dettagli = typeof voce.details === "object" && voce.details !== null ? (voce.details as Record<string, unknown>) : {};
  // Gli avvisi dello stack registrano il nome sotto `stack`, le altre azioni sotto `name`.
  const nome = dettagli.name ?? dettagli.stack;
  // Number(null) è 0: senza resourceId non c'è uno stack a cui puntare.
  const id = voce.resourceId === null ? NaN : Number(voce.resourceId);
  // Lo stack eliminato non ha più una pagina: solo il nome.
  const conLink = voce.resourceType === "stack" && Number.isInteger(id) && voce.action !== "stack.delete";

  if (voce.resourceType === "stack" && typeof nome !== "string") return [voce.action];

  const frase = t.traduci({
    chiave: `attivita.${voce.action}`,
    parametri: {
      attore: voce.actor,
      stack: conLink ? SEGNAPOSTO : typeof nome === "string" ? nome : "",
      revisione: typeof dettagli.revision === "number" ? dettagli.revision : undefined,
    },
  });
  if (frase === null) return [voce.action];
  // Alcune frasi su uno stack non lo nominano (i webhook): niente link da incollare.
  if (!conLink || !frase.includes(SEGNAPOSTO)) return [frase];

  const [prima = "", ...dopo] = frase.split(SEGNAPOSTO);
  return [prima, { stack: nome as string, id }, dopo.join(SEGNAPOSTO)].filter((s) => s !== "");
}
